import React from "react";
import Tooltip from "@mui/material/Tooltip";
import NorthIcon from "@mui/icons-material/North";
import SouthIcon from "@mui/icons-material/South";

// Matches the light tooltip card used across the schedule and maintenance
// surfaces so the Overview reads as part of the same system.
export const overviewTooltipSlotProps = {
  tooltip: {
    sx: {
      bgcolor: "white",
      color: "#1e293b",
      border: "1px solid #e2e8f0",
      borderRadius: "10px",
      boxShadow: "0 12px 28px rgba(15, 23, 42, 0.12)",
      p: 0,
      maxWidth: "none",
    },
  },
  arrow: {
    sx: {
      color: "white",
      "&::before": {
        border: "1px solid #e2e8f0",
        backgroundColor: "white",
        boxSizing: "border-box",
      },
    },
  },
};

export function OverviewTooltip({ children, ...props }) {
  return (
    <Tooltip
      arrow
      disableInteractive
      enterDelay={150}
      enterNextDelay={75}
      slotProps={overviewTooltipSlotProps}
      {...props}
    >
      {children}
    </Tooltip>
  );
}

// Every Overview tooltip shares one width so hovering between the sales row
// and the labor block does not resize the card under the cursor.
export const TOOLTIP_WIDTH_CLASS = "w-[400px]";

export function TooltipCard({ children, className = "" }) {
  return <div className={`px-4 py-3.5 ${className}`}>{children}</div>;
}

export function TooltipHeader({ title, subtitle }) {
  return (
    <div className="mb-3">
      <p className="text-[13px] font-bold leading-tight text-slate-900">
        {title}
      </p>
      {subtitle ? (
        <p className="mt-1 text-[11px] font-medium leading-snug text-slate-500">
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

export function TooltipDivider({ className = "" }) {
  return <div className={`my-2.5 h-px bg-slate-100 ${className}`} />;
}

// Declares the frame of reference for the block beneath it, so a second
// comparison is never read as belonging to the first one's column.
export function TooltipSectionLabel({ children }) {
  return (
    <p className="mb-1.5 text-[9px] font-bold uppercase tracking-wide text-slate-400">
      {children}
    </p>
  );
}

export function TooltipRow({ label, value, muted = false, emphasis = false }) {
  return (
    <div className="flex items-baseline justify-between gap-6 py-[3px] text-xs">
      <span className={muted ? "text-slate-400" : "text-slate-500"}>
        {label}
      </span>
      <span
        className={`shrink-0 tabular-nums ${
          muted
            ? "font-medium text-slate-400"
            : emphasis
            ? "font-bold text-slate-900"
            : "font-semibold text-slate-700"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

export function TooltipNote({ children, tone = "neutral" }) {
  const toneClass =
    tone === "warn"
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : "border-slate-200 bg-slate-50 text-slate-500";

  return (
    <p
      className={`rounded-md border px-2.5 py-2 text-[11px] leading-snug ${toneClass}`}
    >
      {children}
    </p>
  );
}

const DELTA_TONES = {
  up: { text: "text-emerald-700", Icon: NorthIcon },
  down: { text: "text-rose-700", Icon: SouthIcon },
  flat: { text: "text-slate-500", Icon: null },
};

const getDeltaTone = (value) => {
  if (value > 0) return DELTA_TONES.up;
  if (value < 0) return DELTA_TONES.down;
  return DELTA_TONES.flat;
};

// "inline" matches surrounding 11px prose, so a delta sitting inside a
// sentence does not read as a larger, separately weighted element.
const DELTA_SIZES = {
  sm: { text: "text-xs", icon: "12px" },
  lg: { text: "text-[15px]", icon: "15px" },
  inline: { text: "text-[11px]", icon: "11px" },
};

// Arrow plus magnitude, rather than arrow plus a signed number, so a decline
// does not read as a doubled negative ("down -20.7%").
//
// `invert` flips the colour mapping for metrics where exceeding the target is
// the bad outcome, such as labor hours over forecast.
export function DeltaValue({
  value,
  display,
  placeholder = "-",
  size = "sm",
  invert = false,
}) {
  const sizing = DELTA_SIZES[size] || DELTA_SIZES.sm;

  if (value === null || value === undefined) {
    return (
      <span className={`${sizing.text} font-semibold text-slate-400`}>
        {placeholder}
      </span>
    );
  }

  const tone = getDeltaTone(invert ? -value : value);
  const { Icon } = getDeltaTone(value);

  return (
    <span
      className={`inline-flex items-center gap-0.5 font-bold tabular-nums ${tone.text} ${sizing.text}`}
    >
      {Icon ? <Icon style={{ fontSize: sizing.icon }} /> : null}
      {display || `${Math.abs(value).toFixed(1)}%`}
    </span>
  );
}

// Shared by CompareRow and CompareHeader so the headings can never drift out
// of alignment with the values beneath them.
const AMOUNT_COL_CLASS = "w-[84px]";
const DELTA_COL_CLASS = "w-[94px]";

// A single line of the comparison table. Fixed column widths keep the amounts
// and deltas aligned across every row so the table reads as a column.
export function CompareRow({
  label,
  sublabel,
  amount,
  delta,
  deltaDisplay,
  deltaContent,
  deltaInvert = false,
  deltaPlaceholder = "-",
  showDelta = true,
  isBaseline = false,
  emphasis = false,
}) {
  return (
    <div
      className={`flex items-baseline gap-2 rounded-md px-2 py-1.5 ${
        isBaseline ? "bg-slate-50" : ""
      }`}
    >
      <div className="min-w-0 flex-1">
        <p
          className={`truncate text-xs ${
            isBaseline || emphasis
              ? "font-semibold text-slate-900"
              : "font-medium text-slate-600"
          }`}
        >
          {label}
        </p>
        {sublabel ? (
          <p className="truncate text-[10px] font-medium text-slate-400">
            {sublabel}
          </p>
        ) : null}
      </div>
      <p
        className={`${AMOUNT_COL_CLASS} shrink-0 whitespace-nowrap text-right text-xs tabular-nums ${
          isBaseline || emphasis
            ? "font-bold text-slate-900"
            : "font-semibold text-slate-700"
        }`}
      >
        {amount}
      </p>
      {showDelta ? (
        <div className={`${DELTA_COL_CLASS} shrink-0 text-right`}>
          {isBaseline ? (
            <span className="text-sm font-bold text-slate-400">–</span>
          ) : deltaContent !== undefined ? (
            deltaContent
          ) : (
            <DeltaValue
              value={delta}
              display={deltaDisplay}
              invert={deltaInvert}
              placeholder={deltaPlaceholder}
            />
          )}
        </div>
      ) : null}
    </div>
  );
}

// Multi-column variant for tables where each row is one entity and the
// columns are its attributes, rather than one metric compared to a baseline.
export function MetricTableHeader({ labels, colClasses }) {
  return (
    <div className="flex items-baseline gap-2 px-2 pb-1">
      <div className="min-w-0 flex-1" />
      {labels.map((label, index) => (
        <p
          key={label}
          className={`${colClasses[index]} shrink-0 whitespace-nowrap text-right text-[9px] font-bold uppercase tracking-wide text-slate-400`}
        >
          {label}
        </p>
      ))}
    </div>
  );
}

export function MetricTableRow({
  label,
  values,
  colClasses,
  emphasis = false,
  muted = false,
  isBaseline = false,
}) {
  return (
    <div
      className={`flex items-baseline gap-2 rounded-md px-2 py-1.5 ${
        isBaseline ? "bg-slate-50" : ""
      }`}
    >
      <p
        className={`min-w-0 flex-1 truncate text-xs ${
          isBaseline || emphasis
            ? "font-semibold text-slate-900"
            : muted
            ? "font-medium text-slate-400"
            : "font-medium text-slate-600"
        }`}
      >
        {label}
      </p>
      {values.map((value, index) => (
        <div
          key={colClasses[index]}
          className={`${
            colClasses[index]
          } shrink-0 whitespace-nowrap text-right text-xs tabular-nums ${
            emphasis
              ? "font-bold text-slate-900"
              : muted
              ? "font-medium text-slate-400"
              : "font-semibold text-slate-700"
          }`}
        >
          {value}
        </div>
      ))}
    </div>
  );
}

// Column headings for the CompareRow stack. Widths mirror CompareRow exactly.
export function CompareHeader({ amountLabel, deltaLabel, showDelta = true }) {
  return (
    <div className="flex items-baseline gap-2 px-2 pb-1">
      <div className="min-w-0 flex-1" />
      <p
        className={`${AMOUNT_COL_CLASS} shrink-0 whitespace-nowrap text-right text-[9px] font-bold uppercase tracking-wide text-slate-400`}
      >
        {amountLabel}
      </p>
      {showDelta ? (
        <p
          className={`${DELTA_COL_CLASS} shrink-0 whitespace-nowrap text-right text-[9px] font-bold uppercase tracking-wide text-slate-400`}
        >
          {deltaLabel}
        </p>
      ) : null}
    </div>
  );
}

export default OverviewTooltip;
